import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/lib/settings";
import { useApi, errMsg, priceFor, rupiah } from "@/lib/api";
import EventNameSelect from "@/components/order/EventNameSelect";

const FIELDS = [["parent_name", "Pemesan"], ["whatsapp", "WhatsApp"], ["athlete_name", "Atlet"], ["bib_number", "No. BIB"], ["club", "Klub"], ["category", "Kategori"]];

export default function EditOrderForm({ order, onSaved, onCancel }) {
  const api = useApi();
  const { settings } = useSettings();
  const [f, setF] = useState(() => ({ ...Object.fromEntries(FIELDS.map(([k]) => [k, order[k] || ""])), notes: order.notes || "" }));
  const [events, setEvents] = useState(order.events.map((e) => ({ ...e })));
  const [saving, setSaving] = useState(false);
  const days = settings?.drive_links || [];
  const setEv = (i, k, v) => setEvents(events.map((e, j) => (j === i ? { ...e, [k]: v } : e)));

  const save = async () => {
    if (events.some((e) => !e.name || !e.day || !e.photos)) return toast.error("Lengkapi data mata lomba");
    setSaving(true);
    try {
      const { data } = await api("put", `/admin/orders/${order.id}`, { ...f, events });
      toast.success("Pesanan diperbarui");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e, "Gagal menyimpan (periksa semua kolom)")); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 text-sm" data-testid="edit-order-form">
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`edit-${k}-input`} className="h-9 bg-[#121212]" /></div>
        ))}
      </div>
      <div className="space-y-1"><Label className="text-xs">Catatan pembeli</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="edit-notes-input" className="bg-[#121212] min-h-[50px]" /></div>
      {events.map((e, i) => (
        <div key={i} className="bg-[#121212] border border-white/10 p-3 space-y-2" data-testid={`edit-event-${i}`}>
          <div className="flex gap-2">
            <EventNameSelect value={e.name} onChange={(v) => setEv(i, "name", v)} options={settings?.event_options || []} taken={events.map((x) => x.name)} testId={`edit-event-name-${i}`} className="h-9" />
            <Select value={e.day} onValueChange={(v) => setEv(i, "day", v)}>
              <SelectTrigger className="h-9 w-44" data-testid={`edit-event-day-${i}`}><SelectValue placeholder="Hari" /></SelectTrigger>
              <SelectContent>{days.map((d) => <SelectItem key={d.key} value={d.label}>{d.label}</SelectItem>)}{!days.some((d) => d.label === e.day) && e.day && <SelectItem value={e.day}>{e.day}</SelectItem>}</SelectContent>
            </Select>
            {events.length > 1 && <button onClick={() => setEvents(events.filter((_, j) => j !== i))} data-testid={`edit-event-remove-${i}`} className="px-1 text-neutral-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
          </div>
          <Textarea value={e.photos} onChange={(x) => setEv(i, "photos", x.target.value)} placeholder="Nama file foto" data-testid={`edit-event-photos-${i}`} className="min-h-[50px]" />
        </div>
      ))}
      {events.length < 4 && <button onClick={() => setEvents([...events, { name: "", day: "", photos: "" }])} data-testid="edit-add-event-btn" className="text-[#FF5A00] flex items-center gap-1"><Plus className="w-4 h-4" /> Tambah mata lomba</button>}
      <p className="text-neutral-400">Total baru: <b className="text-[#FF5A00]" data-testid="edit-total">{rupiah(events.length === order.event_count ? order.total : priceFor(events.length, settings?.prices))}</b></p>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} data-testid="edit-save-btn" className="flex-1 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 py-2 font-display font-bold uppercase flex items-center justify-center gap-2 transition-colors">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Simpan</button>
        <button onClick={onCancel} data-testid="edit-cancel-btn" className="flex-1 border border-white/20 hover:border-white py-2 font-display font-bold uppercase transition-colors">Batal</button>
      </div>
    </div>
  );
}
